'use client';

import { Link } from '@chakra-ui/next-js';
import { Box, Flex, HStack, Icon, Image, Stack, Text } from '@chakra-ui/react';
import {
  getProjectPath,
  getLocationDisplayText,
  formatProjectPriceRange,
  formatProjectLivingSpaceRange,
  formatProjectRoomsRange,
  getFilteredProjectImages,
} from '@habity/services';
import { NestedProject } from '@habity/types';
import { ListSkeleton } from '~/components/homes/list/components/ListSkeleton';
import { OverlayStatusBadge } from '~/components/ui/images/buttons/OverlayStatusBadge';
import { useAgency } from '~/hooks/useAgency';
import { useTranslations } from 'next-intl';
import { Gallery } from '~/components/ui/images/Gallery';
import { IconType } from 'react-icons';
import { useInView } from 'react-intersection-observer';
import { getDeveloperLogoUrl } from '~/lib/providers/supabase/avatars';
import { createClient } from '~/lib/providers/supabase/client';

interface ProjectListCard {
  project: NestedProject;
  previewImages?: number;
}

export function ProjectListCard({
  project,
  previewImages = 3,
}: ProjectListCard) {
  const { status } = project || {};
  const images = getFilteredProjectImages(project);
  const { getStatusIcon } = useAgency();
  const t = useTranslations();
  const supabase = createClient();

  const shouldShowDeveloperLogo =
    project?.developer?.has_logo && project?.developer?.id;
  const developerLogoUrl =
    shouldShowDeveloperLogo &&
    project?.developer?.id &&
    project?.developer?.logo_extension
      ? getDeveloperLogoUrl(
          supabase,
          project.developer.id,
          project.developer.logo_extension,
          { width: 160, resize: 'contain', quality: 90 }
        )
      : '';

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0,
  });

  return (
    <Link
      href={getProjectPath(project)}
      _hover={{ textDecoration: 'none' }}
      className="group"
    >
      <Flex
        overflow="hidden"
        flexDirection="column"
        flexGrow={{ base: 0, sm: 1 }}
        justifyContent="flex-start"
        gap="1"
        cursor="pointer"
      >
        <Box ref={ref} backgroundColor="blackAlpha.50" minH="300px">
          <Box position="relative">
            {inView ? (
              <Gallery
                variant="project"
                images={images ?? []}
                imageVariant="list"
                aspectRatio={1 / 1.2}
                imageProps={{ objectFit: 'cover' }}
                borderTopRadius="4px"
                borderBottomRadius="4px"
                maxSlides={previewImages}
                pagination={{
                  type: 'bullets',
                }}
                isLoading={false}
                onClick={() => {}}
              />
            ) : null}
            {status ? (
              <Stack position="absolute" top="3" left="3" zIndex="3">
                <OverlayStatusBadge
                  leftIcon={
                    getStatusIcon(status.name) !== null ? (
                      <Icon
                        boxSize="3"
                        as={getStatusIcon(status.name) as IconType}
                      />
                    ) : null
                  }
                  label={t('Project.Status.' + status.name + '.title')}
                />
              </Stack>
            ) : null}
            {shouldShowDeveloperLogo && developerLogoUrl && (
              <Box
                position="absolute"
                bottom="3"
                right="3"
                zIndex="4"
                bg="white"
                borderRadius="md"
                p="1.5"
                boxShadow="0 2px 8px rgba(0, 0, 0, 0.15)"
              >
                <Image
                  src={developerLogoUrl}
                  alt={`${project?.developer?.name} logo`}
                  maxH="28px"
                  maxW="80px"
                  objectFit="contain"
                />
              </Box>
            )}
          </Box>
        </Box>
        <ProjectCardData project={project} />
      </Flex>
    </Link>
  );
}

interface ProjectCardDataProps {
  project: NestedProject;
  isLoading?: boolean;
}

function ProjectCardData({ project, isLoading = false }: ProjectCardDataProps) {
  const {
    name,
    provider_id,
    area,
    city,
    price_min,
    price_max,
    living_space_min,
    living_space_max,
    rooms_min,
    rooms_max,
    total_homes,
  } = project || {};
  const { municipality } = area || {};

  // TODO: Place as badge over image, like homes

  const numberOfHomesText = total_homes ? `${total_homes} bostäder` : null;
  const priceRangeText = formatProjectPriceRange(price_min, price_max);
  const livingSpaceRangeText = formatProjectLivingSpaceRange(
    living_space_min,
    living_space_max
  );
  const roomsRangeText = formatProjectRoomsRange(rooms_min, rooms_max);

  return (
    <Stack
      spacing="1"
      pt={0}
      pb={1}
      cursor="pointer"
      justifyContent="flex-start"
    >
      {/* Name */}
      <ListSkeleton isLoaded={!isLoading}>
        <Box mt="1" noOfLines={1}>
          <HStack spacing="1" justifyContent="flex-start">
            <Text
              size="md"
              fontWeight="semibold"
              flexShrink="0"
              color="blackAlpha.900"
              lineHeight={1.5}
              noOfLines={1}
              _groupHover={{ textDecoration: 'underline' }}
            >
              {name ?? provider_id}
            </Text>
          </HStack>
        </Box>
      </ListSkeleton>

      {/* Location */}
      <HStack justifyContent="flex-start">
        <ListSkeleton isLoaded={!isLoading}>
          <Text
            color="blackAlpha.800"
            size="sm"
            fontStyle="normal"
            lineHeight={1.2}
            noOfLines={1}
          >
            {[
              project.is_new_construction ? 'Nyproduktion' : 'Bostadsprojekt',
              getLocationDisplayText({
                city,
                municipality: municipality?.name || null,
                area: area ? { name: area.name } : null,
              }),
            ]
              .filter(Boolean)
              .join(', ')}
          </Text>
        </ListSkeleton>
      </HStack>

      {/* Price and Living Space Ranges */}
      {(numberOfHomesText ||
        priceRangeText ||
        livingSpaceRangeText ||
        roomsRangeText) && (
        <HStack alignItems="baseline" justifyContent="flex-start">
          <ListSkeleton isLoaded={!isLoading}>
            <Text color="blackAlpha.600" fontSize="sm" noOfLines={1}>
              {[
                numberOfHomesText,
                roomsRangeText,
                livingSpaceRangeText,
                priceRangeText,
              ]
                .filter(Boolean)
                .join(' • ')}
            </Text>
          </ListSkeleton>
        </HStack>
      )}
    </Stack>
  );
}
